import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ButtonsModule, CardModule, DropdownModule } from 'axis-components';
import { PIP_MASTER_STATES_LIST } from '../../config/constants';
import { RequestService } from '../../_services/request/request.service';
import { PlaygroundService } from '../../_services/playground/playground.service';
import { PipBaseRulesService } from '../../state-disclosures/pip/services/pip-base-rules.service';

// Import all PIP workflow components for testing and demonstration
import { PipSelectionCoveragesComponent } from '../../insured/pip/pip-selection-coverages/pip-selection-coverages.component';
import { PipStateSummaryComponent } from '../../insured/pip/pip-state-summary/pip-state-summary.component';
import { PipOutstandingStateSummaryComponent } from '../../insured/pip/pip-outstanding-state-summary/pip-outstanding-state-summary.component';
import { PipSignatureComponent } from '../../insured/pip/pip-signature/pip-signature.component';
import { ThankYouComponent } from '../../thank-you/thank-you.component';

export enum WorkflowStep {
  SELECTION = 'selection',
  SUMMARY = 'summary',
  OUTSTANDING = 'outstanding',
  SIGNATURE = 'signature',
  THANKYOU = 'thankyou',
}

@Component({
  selector: 'app-pip-playground',
  templateUrl: './pip-playground.component.html',
  styleUrls: ['./pip-playground.component.scss'],
  standalone: true,
  imports: [
    ButtonsModule,
    CardModule,
    CommonModule,
    DropdownModule,
    ReactiveFormsModule,
    PipSelectionCoveragesComponent,
    PipStateSummaryComponent,
    PipOutstandingStateSummaryComponent,
    PipSignatureComponent,
    ThankYouComponent,
  ],
})
export class PipPlaygroundComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  // Form for controlling which states to test in playground mode
  public playgroundForm: FormGroup;

  // Dropdown options for state selection - maps state names to label/value format
  public stateList = PIP_MASTER_STATES_LIST.filter(
    (state) => state && typeof state === 'string' && state.trim() !== '',
  ).map((state, index) => ({
    id: (index + 1).toString(),
    name: state,
    state: state,
  }));

  // Controls visibility of each step in the PIP workflow
  public summaryVisible = false;
  public outstandingVisible = false;
  public signatureVisible = false;
  public thankYouVisible = false;

  // Direct references to child components for programmatic control
  @ViewChild(PipSelectionCoveragesComponent)
  selectionComponent!: PipSelectionCoveragesComponent;

  @ViewChild(PipStateSummaryComponent)
  summaryComponent!: PipStateSummaryComponent;

  @ViewChild(PipOutstandingStateSummaryComponent)
  outstandingComponent!: PipOutstandingStateSummaryComponent;

  @ViewChild(PipSignatureComponent)
  signatureComponent!: PipSignatureComponent;

  @ViewChild(ThankYouComponent)
  thankYouComponent!: ThankYouComponent;

  // DOM element references for smooth scrolling between workflow steps
  @ViewChild('selectionCard', { read: ElementRef })
  selectionCardRef!: ElementRef;

  @ViewChild('summaryCard', { read: ElementRef })
  summaryCardRef!: ElementRef;

  @ViewChild('outstandingCard', { read: ElementRef })
  outstandingCardRef!: ElementRef;

  @ViewChild('signatureCard', { read: ElementRef })
  signatureCardRef!: ElementRef;

  @ViewChild('thankYouCard', { read: ElementRef })
  thankYouCardRef!: ElementRef;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private requestService: RequestService,
    private playgroundService: PlaygroundService,
    private baseRulesService: PipBaseRulesService,
  ) {
    this.playgroundForm = this.fb.group({
      selectedState: [[]], // Array for multiselect
    });
  }

  ngOnInit(): void {
    // Enable playground mode to bypass normal routing and request validation
    this.baseRulesService.isPlayground = true;
    this.goTop();

    // Watch for state selection changes and update the workflow accordingly
    this.playgroundForm
      .get('selectedState')
      ?.valueChanges.subscribe((value) => {
        this.processSelectedStates(value);
      });
  }

  ngOnDestroy(): void {
    // Clean up playground state to prevent interference with normal app flow
    this.playgroundService.clearPlaygroundPIPStates();
    this.baseRulesService.isPlayground = false;
  }

  ngAfterViewInit(): void {
    // In playground mode, components emit events instead of navigating
  }

  // Methods to control the step-by-step workflow visibility
  showSummary(): void {
    this.summaryVisible = true;

    // Refresh summary to display only currently selected states
    if (this.summaryComponent) {
      this.refreshSummaryComponent();
    }

    this.scrollToElement(WorkflowStep.SUMMARY);
  }

  hideSummary(): void {
    this.summaryVisible = false;
    this.outstandingVisible = false; // Hide subsequent steps when going back
    this.scrollToElement(WorkflowStep.SELECTION);
  }

  showOutstanding(): void {
    this.outstandingVisible = true;
    this.scrollToElement(WorkflowStep.OUTSTANDING);
  }

  hideOutstanding(): void {
    this.outstandingVisible = false;
    this.signatureVisible = false;
    this.thankYouVisible = false;

    // Update summary display when returning from outstanding step
    this.refreshSummaryComponent();

    this.scrollToElement(WorkflowStep.SUMMARY);
  }

  showSignature(): void {
    this.signatureVisible = true;
    this.scrollToElement(WorkflowStep.SIGNATURE);
  }

  hideSignature(): void {
    this.signatureVisible = false;
    this.thankYouVisible = false;
    this.scrollToElement(WorkflowStep.OUTSTANDING);
  }

  showThankYou(): void {
    this.thankYouVisible = true;
    this.scrollToElement(WorkflowStep.THANKYOU);
  }

  // Handlers for events emitted by child components in playground mode
  onSelectionContinue(): void {
    this.showSummary();
  }

  onSelectionDataChanged(): void {
    // Clear all subsequent workflow steps when user changes coverage selections
    this.summaryVisible = false;
    this.outstandingVisible = false;
    this.signatureVisible = false;
    this.thankYouVisible = false;
  }

  onSummaryContinue(): void {
    this.showOutstanding();
  }

  onOutstandingContinue(): void {
    this.showSignature();
  }

  onSignatureContinue(): void {
    this.showThankYou();
  }

  onThankYouComplete(): void {
    this.completeWorkflow();
  }

  completeWorkflow(): void {
    this.savePlaygroundData();
  }

  processSelectedStates(selectedStates: any[]): void {
    if (!selectedStates || !Array.isArray(selectedStates)) {
      this.playgroundService.clearPlaygroundPIPStates();
      return;
    }

    // Convert dropdown selection objects to plain state name strings
    // Handles both string values and objects with label/value properties
    const stateNames = selectedStates
      .map((stateObj) => {
        if (typeof stateObj === 'string') {
          return stateObj;
        } else if (stateObj && stateObj.name) {
          return stateObj.name;
        } else if (stateObj && stateObj.state) {
          return stateObj.state;
        }
        return null;
      })
      .filter((state) => state !== null);

    if (stateNames.length > 0) {
      // Clear cached rules and data to ensure fresh state-specific content loads
      this.baseRulesService.savedBaseRules = {};
      this.baseRulesService.dataDump = {} as any;

      this.playgroundService.setPlaygroundPIPStates(stateNames);

      // Hide all workflow steps when changing state selection
      this.summaryVisible = false;
      this.outstandingVisible = false;
      this.signatureVisible = false;
      this.thankYouVisible = false;
    } else {
      this.playgroundService.clearPlaygroundPIPStates();
    }
  }

  onStateChange(selectedStates: any): void {
    // Sync form control with the new selection
    this.playgroundForm.patchValue({ selectedState: selectedStates });

    // Convert to array format and pass to playground service
    const stateNames = Array.isArray(selectedStates)
      ? selectedStates
      : [selectedStates];

    this.playgroundService.setPlaygroundPIPStates(
      stateNames.filter((state) => state),
    );

    // Reset workflow visibility when user changes states
    this.summaryVisible = false;
    this.outstandingVisible = false;
    this.signatureVisible = false;
    this.thankYouVisible = false;
  }

  resetPlayground(): void {
    // Full page refresh ensures complete state reset - simpler than manual cleanup
    window.location.reload();
  }

  private refreshSummaryComponent(): void {
    if (this.summaryComponent && this.summaryComponent.stateComponent) {
      // Remove all dynamically created state components
      this.summaryComponent.stateComponent.clear();

      // Recreate components based on currently selected states
      setTimeout(() => {
        this.summaryComponent.setupDynamicComponents();
      }, 0);
    }
  }

  savePlaygroundData(): void {
    // Placeholder for saving playground session data - implement as needed
  }

  private scrollToElement(elementType: WorkflowStep): void {
    // Attempt to scroll using ViewChild references first for best performance
    const elementRefMap = {
      [WorkflowStep.SELECTION]: this.selectionCardRef,
      [WorkflowStep.SUMMARY]: this.summaryCardRef,
      [WorkflowStep.OUTSTANDING]: this.outstandingCardRef,
      [WorkflowStep.SIGNATURE]: this.signatureCardRef,
      [WorkflowStep.THANKYOU]: this.thankYouCardRef,
    };

    const elementRef = elementRefMap[elementType];

    if (elementRef?.nativeElement) {
      elementRef.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      return;
    }

    // For components that are conditionally rendered, wait briefly then try again
    setTimeout(() => {
      const retryRef = elementRefMap[elementType];
      if (retryRef?.nativeElement) {
        retryRef.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        return;
      }

      // Fallback: query DOM directly by component selectors
      const elementMap = {
        [WorkflowStep.SELECTION]: 'axis-card:has(app-pip-selection-coverages)',
        [WorkflowStep.SUMMARY]: 'axis-card:has(app-pip-state-summary)',
        [WorkflowStep.OUTSTANDING]:
          'axis-card:has(app-pip-outstanding-state-summary)',
        [WorkflowStep.SIGNATURE]: 'axis-card:has(app-pip-signature)',
        [WorkflowStep.THANKYOU]: 'axis-card:has(app-thank-you)',
      };

      const element = document.querySelector(elementMap[elementType]);
      element?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 50);
  }

  goTop(): void {
    window.scrollTo(0, 0);
  }
}
